import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { ReactNode } from "react";

import { analytics } from "MVVM/Analytics";
import { ESupportedGames, IClip } from "MVVM/@types";
import { MatchHistoryMatchThumbnailState } from "MVVM/State/matchHistory";
import { URLS } from "MVVM/Utilities/Maps";
import { DynamicSignupOptions } from "MVVM/Views/Authentication/Components/DynamicSignupOptions";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { MomentDetails } from "../MomentDetails";
import { withReturn } from "MVVM/Utilities/Auth/loginRedirect";

export const MatchHistoryLoginModal = ({
  game,
  moment,
  clip,
  title,
  defaultTitle,
  subtitle,
  defaultSubtitle,
  matchTitle,
}: {
  game: ESupportedGames;
  moment?: TMoment;
  clip?: IClip;
  title: string;
  defaultTitle: string;
  subtitle: string;
  defaultSubtitle: string;
  matchTitle?: ReactNode;
}) => {
  const history = useHistory();
  const matchThumbnail = useRecoilValue(MatchHistoryMatchThumbnailState);
  const { t } = useTranslation();

  const getPlatform = () => {
    if (game === ESupportedGames.CS2) return "Steam";
    if (game === ESupportedGames.LOL) return "Riot";
    if (game === ESupportedGames.FORTNITE) return "Epic";
    return "";
  };

  const thumbnailUrl =
    clip?.thumbnailStandardUrl ||
    matchThumbnail ||
    `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/homepage/mh.png`;

  return (
    <Container>
      <ChampionBanner
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--Gray-750, #1c2a34)), url(${thumbnailUrl})`,
        }}
      />
      {!!moment && <MomentDetails moment={moment} />}
      {!!matchTitle && !moment && (
        <StyledTitleText variant="text.large3" fontWeight={700}>
          {matchTitle}
        </StyledTitleText>
      )}
      <Flex
        style={{ padding: "12px 24px 24px", gap: "24px" }}
        flexDirection="column"
      >
        <Flex flexDirection="column" textAlign="center" style={{ gap: "12px" }}>
          <Title color="ice" variant="text.titleSmall">
            {t(title, defaultTitle)}
          </Title>
          <Subtitle color="chalk" variant="text.paragraphMedium">
            {t(subtitle, defaultSubtitle)}
          </Subtitle>
        </Flex>
        <DynamicSignupOptions game={game} view="claim" />
        <Flex style={{ gap: 10 }} justifyContent="center">
          <Text color="chalk" variant={"text.labelMedium"}>
            {t("matchHistoryLoginModal.signupText", {
              platform: getPlatform(),
              defaultValue: `Don't want to login with ${getPlatform()}?`,
            })}
          </Text>
          <LoginText
            variant={"text.labelMedium"}
            color="envy"
            onClick={() => {
              analytics.track("Match History - Login Modal", {
                type: "signup",
                action: "click",
              });
              history.push(withReturn(URLS.SIGNUP));
            }}
          >
            {t("signup.signup", "Sign up")}
          </LoginText>
        </Flex>
      </Flex>
    </Container>
  );
};

const Container = styled(Flex)`
  position: relative;
  width: 448px;
  flex-direction: column;
  background-color: var(--Gray-750, #1c2a34);
  text-align: center;
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};

  @media (max-width: 500px) {
    width: 390px;
  }

  @media (max-width: 425px) {
    width: 100%;
  }
`;

const ChampionBanner = styled(Flex)`
  background-size: cover;
  height: 192px;
  width: 100%;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
`;

const LoginText = styled(Text)`
  cursor: pointer;
  margin-right: 15px;
  min-width: 55px;

  @media (max-width: 425px) {
    margin-right: 0px;
  }
`;

const Title = styled(Text)`
  @media (max-width: 425px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const Subtitle = styled(Text)`
  @media (max-width: 425px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const StyledTitleText = styled(Text)`
  left: 25%;
  position: absolute;
  top: 20%;
`;
